// The daily comparison with Klaviyo (7.2 M4), in words: an outcome, a rate, a
// day's standing, "élesíthető?", a time difference, one point of a timeline —
// and the two timelines laid on one time axis, the difference highlighted.
// The reasons come with their Hungarian words from the API
// (`emc_db.flows.compare.REASONS`); a code without words stays visible as it is.
//
// Pure functions: tests/admin/test_flows_compare_model.py runs them on Node.

import type {
  FlowComparisonItem,
  FlowComparisonOutcome,
  FlowComparisonSummaryRow,
  FlowComparisonVersion,
  FlowComparisonWindow,
  FlowTimelineItem,
} from "@/lib/api";
import type { Tone } from "@/components/emc/status";
import { decisionReasonText, runOutcomeText, runStateText, stepResultText } from "@/pages/flows/shadowModel";

// --- an outcome ------------------------------------------------------------------

const OUTCOME: Record<FlowComparisonOutcome, { label: string; tone: Tone }> = {
  match: { label: "egyezik", tone: "good" },
  time_diff: { label: "időeltérés", tone: "warn" },
  klaviyo_only: { label: "csak a Klaviyo küldte", tone: "danger" },
  emc_only: { label: "csak mi küldenénk", tone: "danger" },
  out_of_window: { label: "ablakon kívül", tone: "muted" },
  unknown_message: { label: "ismeretlen levél", tone: "warn" },
};

/** The order the outcomes are shown in: agreement first, the rest after. */
export const OUTCOMES = Object.keys(OUTCOME) as FlowComparisonOutcome[];

export function outcomeLook(outcome: string): { label: string; tone: Tone } {
  return OUTCOME[outcome as FlowComparisonOutcome] ?? { label: outcome, tone: "muted" };
}

// --- numbers ---------------------------------------------------------------------

/** "93,4%", or "—" when nothing was compared. */
export function rateText(window: Pick<FlowComparisonWindow, "match_rate"> | null | undefined): string {
  if (!window || window.match_rate === null || window.match_rate === undefined) return "—";
  return `${(window.match_rate * 100).toLocaleString("hu-HU", { maximumFractionDigits: 1 })}%`;
}

/** A rate's tone: all match good, from 95% a warning, below it danger. */
export function rateTone(window: Pick<FlowComparisonWindow, "match_rate" | "unexplained"> | null | undefined): Tone {
  if (!window || window.match_rate === null || window.match_rate === undefined) return "muted";
  if (window.unexplained === 0) return "good";
  return window.match_rate >= 0.95 ? "warn" : "danger";
}

/** Klaviyo's moment minus ours: "+3 perc", "−1 óra 20 perc", "+2 nap 1 óra". */
export function deltaText(seconds: number | null | undefined): string {
  if (seconds === null || seconds === undefined) return "—";
  const sign = seconds < 0 ? "−" : "+";
  let left = Math.round(Math.abs(seconds) / 60);
  if (left === 0) return `${sign}${Math.abs(seconds)} mp`;
  const days = Math.floor(left / 1440);
  left -= days * 1440;
  const hours = Math.floor(left / 60);
  const minutes = left - hours * 60;
  const parts = [];
  if (days) parts.push(`${days} nap`);
  if (hours) parts.push(`${hours} óra`);
  if (minutes && !days) parts.push(`${minutes} perc`);
  return `${sign}${parts.join(" ")}`;
}

// --- a day, a flow ---------------------------------------------------------------

const DAY_STATUS: Record<string, { label: string; tone: Tone; title: string }> = {
  final: {
    label: "végleges",
    tone: "good",
    title: "Minden jel és a motor 48 órával túl van a napon: a szám nem változik.",
  },
  partial: {
    label: "eddig",
    tone: "scheduled",
    title: "A nap még nem zárt le (a jelek vagy a motor nincs 48 órával túl rajta); a szám még változhat.",
  },
  pending: { label: "vár", tone: "muted", title: "Még nem vethető össze." },
};

export function dayStatusLook(status: string | null | undefined): { label: string; tone: Tone; title: string } {
  return DAY_STATUS[status ?? ""] ?? { label: status ?? "—", tone: "muted", title: "" };
}

/** "élesíthető?" in words: yes, or what is missing. */
export function readyLook(row: Pick<FlowComparisonSummaryRow, "ready" | "ready_detail">): {
  label: string;
  tone: Tone;
  title: string;
} {
  const detail = row.ready_detail;
  if (row.ready) {
    return {
      label: "élesíthető",
      tone: "good",
      title: `Az utolsó ${detail.days} végleges napon nincs nem magyarázott eltérés és hiányzó adat, ${detail.sends} összevetett küldés.`,
    };
  }
  const missing: string[] = [];
  if (detail.unexplained > 0) missing.push(`${detail.unexplained} nem magyarázott eltérés`);
  // What the facts cannot judge holds it back too (the owner, 2026-11-02).
  if ((detail.missing_info ?? 0) > 0) missing.push(`${detail.missing_info} hiányzó adat`);
  if (detail.days < detail.days_needed) missing.push(`${detail.days}/${detail.days_needed} végleges nap`);
  if (detail.sends < detail.sends_needed) missing.push(`${detail.sends}/${detail.sends_needed} küldés`);
  return {
    label: "még nem",
    tone: detail.unexplained > 0 ? "danger" : "muted",
    title: missing.join(" · ") || "még nem",
  };
}

/** The versions published on a day, by the day (YYYY-MM-DD, UTC): where an
 * editor change starts to show. */
export function versionMarks(versions: FlowComparisonVersion[]): Record<string, number[]> {
  const marks: Record<string, number[]> = {};
  for (const version of versions) {
    if (!version.published_at) continue;
    const day = version.published_at.slice(0, 10);
    (marks[day] ??= []).push(version.number);
  }
  return marks;
}

// --- the two timelines ------------------------------------------------------------

/** One point of a timeline in words. */
export function timelineText(item: FlowTimelineItem): string {
  switch (item.kind) {
    case "event": {
      const step = item.step_id ? ` · lépés #${item.step_id}` : "";
      const receipt = item.metric === "Received Email" || item.metric === "Bounced Email";
      const which = receipt && !item.flow_source_id ? " (kampány)" : "";
      return `${item.metric ?? "esemény"}${which}${item.this_flow ? step : ""}`;
    }
    case "join":
      return `feliratkozás: ${item.metric ?? ""} ${item.group_id ?? ""}`.trim();
    case "entry":
      return `futás #${item.run_id}: indító (${item.trigger_kind ?? "?"}) · most: ${runStateText(item.state)}${
        item.outcome_reason ? ` · ${runOutcomeText(item.outcome_reason)}` : ""
      }`;
    case "step":
      return `#${item.step_id}: ${stepResultText(item.result ?? "")}`;
    case "decision":
      return `levél #${item.step_id}: ${decisionReasonText(item.reason ?? "")}`;
    case "move":
      return `${item.from ?? "—"} → ${item.to ?? "—"}`;
    default:
      return item.kind;
  }
}

export interface TimelineRow {
  at: string | null;
  klaviyo: FlowTimelineItem | null;
  emc: FlowTimelineItem | null;
  /** The difference being looked at: its receipt or its decision. */
  highlight: boolean;
}

/** Both sides on one axis: one row per point, in time order, its side's cell
 * filled. `selected` marks the receipt and the decision of one comparison. */
export function timelineRows(
  items: FlowTimelineItem[],
  selected: Pick<FlowComparisonItem, "decision_id" | "klaviyo_event_id"> | null,
  options: { moves?: boolean } = {},
): TimelineRow[] {
  const shown = items.filter((item) => options.moves || item.kind !== "move");
  const sorted = [...shown].sort((a, b) => (a.at ?? "").localeCompare(b.at ?? ""));
  return sorted.map((item) => {
    const highlight =
      selected !== null &&
      ((item.side === "klaviyo" && item.kind === "event" && item.id === selected.klaviyo_event_id) ||
        (item.side === "emc" && item.kind === "decision" && item.id === selected.decision_id));
    return {
      at: item.at,
      klaviyo: item.side === "klaviyo" ? item : null,
      emc: item.side === "emc" ? item : null,
      highlight,
    };
  });
}
